import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAllBranches } from "../contexts/BranchesContext";
import { useObligoData } from "../hooks/obligo/useObligoData";
import { Alert, IconButton, TextField, InputAdornment, Tooltip, Box, CircularProgress } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PersonIcon from "@mui/icons-material/Person";
import CustomSnackbar from "../components/CustomSnackbar";
import { CustomerDetails } from "../components/Obligo/Customer";
import { Obligo } from "../components/Obligo/Obligo";
import { BusinessInformation } from "../components/Obligo/BusinessInformation";
import { OpenTransaction } from "../components/Obligo/OpenTransaction";
import { OpenInvoices } from "../components/Obligo/OpenInvoices";
import { DelayedOrders } from "../components/Obligo/DelayedOrders";
import { OpenOrders } from "../components/Obligo/OpenOrders";
import { CollateralDetails } from "../components/Obligo/CollateralDetails";

/**
 * ObligoException Page
 * Displays comprehensive financial data for a customer including:
 * - Customer details, obligo status, business information
 * - Open transactions, invoices, orders, and collateral details
 */
const ObligoException = () => {
  const navigate = useNavigate();
  const { custId: urlCustId } = useParams();
  const { allBranches } = useAllBranches();
  
  const [custId, setCustId] = useState(urlCustId || "");
  const [submit, setSubmit] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const { data, loading, error, snackbar, fetchAllData, closeSnackbar } = useObligoData(custId, allBranches);

  // Fetch data when URL custId changes or submit is triggered
  useEffect(() => {
    if (urlCustId) {
      setCustId(urlCustId);
      setHasSubmitted(true);
      fetchAllData();
    }
  }, [urlCustId, submit]);

  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      handleSubmit();
    }
  };

  const handleSubmit = () => {
    setSubmit(!submit);
    navigate(`/ObligoException/${custId}`);
  };

  return (
    <div>
      <Box
        sx={{
          maxWidth: "100%",
          marginX: "auto",
          paddingX: "20px",
          marginTop: "5%",
        }}
      >
        <h1 className="welcome-text">ריכוז נתונים כספיים ללקוח</h1>

        <Box
          sx={{
            padding: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            backgroundColor: "#fff",
            borderRadius: 2,
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
            maxWidth: 300,
            margin: "0 auto",
            marginBottom: 3,
            marginTop: 3,
            paddingX: 2,
            paddingY: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              width: "100%",
              gap: 2,
            }}
          >
            <TextField
              fullWidth
              variant="outlined"
              type="text"
              value={custId}
              onChange={(e) => setCustId(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder="הכנס מספר לקוח"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon color="primary" />
                  </InputAdornment>
                ),
              }}
            />
            <Tooltip title="Search customer">
              <IconButton color="primary" onClick={handleSubmit}>
                <SearchIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        
        {loading ? (
          <CircularProgress
            size={80}
            style={{ margin: "auto", display: "block" }}
          />
        ) : (
          hasSubmitted && !error && (
            <div>
              {/* Customer Details */}
              <CustomerDetails fnccust={data.fnccust} />
              
              {/* Customer Obligo */}
              <Obligo
                obligo={data.obligoValue}
                obligoPerBranch={data.obligoPerBranch}
              />
              
              {/* Business Information */}
              <BusinessInformation fnccust={data.fnccust} />
              
              {/* Open Transactions */}
              <OpenTransaction
                OpenTransaction={data.openTransaction}
                OpenTransactionPerBranch={data.transPerBranch}
                statusError={allBranches?.length === 0}
              />
              
              {/* Open Invoices */}
              <OpenInvoices
                OpenInvoices={data.openInvoices}
                OpenInvoicesPerBranch={data.invoicesPerBranch}
                statusError={allBranches?.length === 0}
              />
              
              {/* Delayed Orders */}
              <DelayedOrders Orders={data.delayOrders} />
              
              {/* Open Orders */}
              <OpenOrders OpenOrders={data.openOrder} />
              
              {/* Collateral Details (without VAT) */}
              <CollateralDetails
                collateralDetails={data.collateralDetails}
                statusError={allBranches?.length === 0}
              />
            </div>
          )
        )}
        
        <CustomSnackbar
          open={snackbar.open}
          message={snackbar.message}
          severity={"error"}
          onClose={closeSnackbar}
        />
      </Box>
    </div>
  );
};

export default ObligoException;
